import { Component, HostBinding, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { SportModel } from '@frontend/sports/betting-offer/feature/model';
import { BetBuilderConfig, EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { ListItemComponent } from '@frontend/sports/common/ui/sports-list-items';
import { ComponentLoaderComponent } from '@frontend/sports/deferred/feature/component-loader';
import { NOT_APPLICABLE, TrackReferringACtionDirective, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { RxIf } from '@rx-angular/template/if';
import { Subject, map, startWith } from 'rxjs';

import { BetBuilderConfigService } from '../bet-builder/services/bet-builder-config.service';
import { ErrorComponent } from '../common/error/error.component';
import { LiveNowLinkRegisterService } from '../live-now-item/live-now-link-register.service';
import { Coupon, CouponList, CouponType } from './coupon.model';
import { CouponsUtilsService } from './coupons.utils.service';

@HooksWireup()
@Component({
    selector: 'ms-coupon-list',
    templateUrl: 'coupon-list.component.html',
    imports: [RxIf, ListItemComponent, ErrorComponent, ComponentLoaderComponent, TrackReferringACtionDirective],
})
export class CouponListComponent extends OnRouteResolve<CouponList> implements OnDestroy, OnInit {
    sport: SportModel;
    coupons: Coupon[];
    title: string;
    couponType = CouponType;
    showBetbuilderNewBadge: boolean;
    render$ = new Subject();
    showLoader$ = this.render$.pipe(
        map(() => false),
        startWith(true),
    );

    epcotEnabled: boolean;

    @HostBinding('class') className = 'coupon-list';
    cleanedAfterTrack = signal(false);

    constructor(
        public sitecore: Sitecore,
        route: ActivatedRoute,
        private liveButton: LiveNowLinkRegisterService,
        private betBuilderConfig: BetBuilderConfig,
        private trackingService: TrackingService,
        public utilsService: CouponsUtilsService,
        private betBuilderConfigService: BetBuilderConfigService,
        private epcotConfig: EpcotConfig,
    ) {
        super(route);
        this.epcotEnabled = this.epcotConfig.isEnabled;
    }

    set list(value: CouponList) {
        if (!value?.coupons?.length) {
            return;
        }

        this.sport = value.sport;
        this.coupons = value.coupons;
        this.title = this.sitecore.coupons.coupons.replace('{0}', this.sport.name);
    }

    onRouteResolved(model: CouponList | undefined): void {
        if (!model) {
            return;
        }
        this.list = model;
        this.liveButton.addLiveButton(model.counts);
    }

    getTracking(coupon: Coupon): string | undefined {
        return coupon.type === this.couponType.BetBuilder ? 'BuildABet_CouponPage_List_Click' : 'M2_ListItemlinks';
    }

    getTitle(coupon: Coupon): string | undefined {
        return coupon.type === this.couponType.BetBuilder ? undefined : coupon.title;
    }

    ngOnDestroy(): void {
        this.liveButton.removeLiveButton();
    }

    ngOnInit(): void {
        this.showBetbuilderNewBadge = Boolean(
            this.sport &&
                this.betBuilderConfigService.isBetBuilderEnabled(this.sport.id) &&
                this.betBuilderConfig.isBetBuilderNewBadgeEnabledForSport?.includes(this.sport.id),
        );
    }

    trackCouponClick(coupon: Coupon): void {
        this.cleanedAfterTrack.update(() => this.utilsService.getUrl(coupon, this.sport).indexOf('http') === 0 || false);
        if (coupon.type === CouponType.FormCoupon) {
            this.trackingService.triggerEventWithCleaning(trackingConstants.EVENT_TRACKING, {
                [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'sports coupons',
                [trackingConstants.COMPONENT_LABEL_EVENT]: 'FormCoupon',
                [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
                [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
                [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
                [trackingConstants.COMPONENT_EVENT_DETAILS]: 'coupon',
                [trackingConstants.COMPONENT_URL_CLICKED]: this.utilsService.getUrl(coupon, this.sport),
            });
        }

        if (coupon.type === CouponType.BetBuilder) {
            this.trackingService.triggerEventWithCleaning(trackingConstants.EVENT_TRACKING, {
                [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'betbuilder',
                [trackingConstants.COMPONENT_LABEL_EVENT]: 'entry point',
                [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
                [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
                [trackingConstants.COMPONENT_LOCATION_EVENT]: 'coupons',
                [trackingConstants.COMPONENT_EVENT_DETAILS]: 'build a bet matches',
                [trackingConstants.COMPONENT_URL_CLICKED]: this.utilsService.getUrl(coupon, this.sport),
                [trackingConstants.SPORT_ID]: this.sport.id.toString(),
            });
        }
    }
}
